// === WORKFLOW SUMMARY — offer-chain document math ===
// The SAA money math applied around the offer BFF calls (calculate / package / quote) plus the
// pure response-extraction helpers. Moved verbatim from WorkflowSummaryScreen.tsx; the only
// seams are the injected opts (config / workflow data / rbDekkingen / selectedProducts / ppEkst)
// replacing the component closure, so everything is golden-testable offline (see calc.test.ts).
// The three document functions MUTATE the passed document in place — callers rely on that.
import { drCoverage } from './enrich';
import { restoreDoorlopendeReisInputs } from '../doorlopendereis/calc';
import { restoreOngevallenInputs, restoreOngevallenInsured } from '../ongevallen/calc';
import { orderSaaCoveragesInDocument } from '../../../utils/autoSectionOrder';
import { applyPkTinv } from '../../../utils/pkTinv';
import { applyPolicyCostAllocationToProducts, computePolicyCostAllocation } from '../../../utils/policyCost';
import { keepSelectedEcRow } from '../inboedel/coverages';

/**
 * Fix PP-level premium totals before the package call.
 * The BFF calculateRelation may return PP_NTP=0 while section-level NTPs are correct.
 * The package endpoint uses PP_NTP to compute PP_TTOT, so we must aggregate from sections.
 * IMPORTANT: Only sum BASE sections per product type — NOT optional coverages.
 * Including optional coverage sections inflates PP_NTP, causing the BFF to return
 * inflated section-level values that break coverage cleaning.
 * Mutates calculationDocument in place. Seams: opts.config (inboedel/woonhuis discount) and
 * opts.rbDekkingen (was data['rechtsbijstand']?.Supportsection?.Dekkingen).
 */
export const aggregatePpTotalsForPackage = (
  calculationDocument: any,
  opts: { config: any; rbDekkingen: any },
): void => {
  const { config } = opts;
  const contractArray = calculationDocument.Contract || [];
  for (const contract of contractArray) {
    const pp = contract?.PP;
    if (!pp) continue;
    // Determine base sections per product type
    let sections: string[];
    let wcBaseSeed: { wcBaseNTP: number; wcBaseTPP: number; wcBaseTASS: number; wcBaseBTP: number; wcBaseTTOT: number } | null = null;
    if (pp.IP) {
      // Inboedel: PP_NTP/PP_TASS from BFF are already correct — skip recalculation
      // The BFF includes all present sections in its totals, and manual summing
      // misses EC array entries (Mobiel + Smart Home) and causes mismatches
      // Apply hidden inboedel discount to section-level values before package call (only when applyToData is true).
      // Skip when the InboedelScreen already baked it (_discountBaked marker) to avoid double-applying.
      const ibDiscount = (config as any)?.inboedelDiscount;
      // applyToData=true (SAA) means InboedelScreen already baked the discount into the data;
      // the _discountBaked marker gets stripped by the schema whitelist, so fall back to
      // applyToData to avoid double-discounting (same guard as the woonhuis block below).
      const ibAlreadyBaked = (pp as any)?._discountBaked === true || ibDiscount?.applyToData === true;
      if (!ibAlreadyBaked && ibDiscount?.enabled && ibDiscount?.percentage > 0 && ibDiscount?.applyToData !== false) {
        const ibFactor = 1 - ibDiscount.percentage / 100;
        const ibExcluded: string[] = ibDiscount.excludeCoverages || [];
        const ibRound = (v: number) => Math.round(v * ibFactor * 100) / 100;
        for (const s of ['IP', 'CY', 'BI', 'GP', 'LS', 'OP', 'HU', 'KB']) {
          if (ibExcluded.includes(s)) continue;
          const sec = Array.isArray(pp[s]) ? pp[s][0] : pp[s];
          if (!sec) continue;
          for (const suffix of ['_NTP', '_TPP', '_TASS', '_BTP', '_TTOT']) {
            const key = `${s}${suffix}`;
            if (sec[key] !== undefined) sec[key] = ibRound(Number(sec[key]));
          }
        }
        if (!ibExcluded.includes('EC') && Array.isArray(pp.EC)) {
          for (const ec of pp.EC) {
            for (const suffix of ['_NTP', '_TPP', '_TASS', '_BTP', '_TTOT']) {
              const key = `EC${suffix}`;
              if (ec[key] !== undefined) ec[key] = ibRound(Number(ec[key]));
            }
          }
        }
        // Recalculate PP totals from discounted sections
        let ppNtp = 0, ppTpp = 0, ppTass = 0, ppBtp = 0, ppTtot = 0;
        for (const s of ['IP', 'CY', 'BI', 'GP', 'LS', 'OP', 'HU', 'KB']) {
          const sec = Array.isArray(pp[s]) ? pp[s][0] : pp[s];
          if (sec) {
            ppNtp += Number(sec[`${s}_NTP`] || 0);
            ppTpp += Number(sec[`${s}_TPP`] || 0);
            ppTass += Number(sec[`${s}_TASS`] || 0);
            ppBtp += Number(sec[`${s}_BTP`] || 0);
            ppTtot += Number(sec[`${s}_TTOT`] || 0);
          }
        }
        if (Array.isArray(pp.EC)) {
          for (const ec of pp.EC) {
            ppNtp += Number(ec.EC_NTP || 0);
            ppTpp += Number(ec.EC_TPP || 0);
            ppTass += Number(ec.EC_TASS || 0);
            ppBtp += Number(ec.EC_BTP || 0);
            ppTtot += Number(ec.EC_TTOT || 0);
          }
        }
        // Round to 2 decimals — section sums can land as 4.830000000000001 etc. from JS float.
        const round2 = (v: number) => Math.round(v * 100) / 100;
        pp.PP_NTP = round2(ppNtp);
        pp.PP_TPP = round2(ppTpp);
        pp.PP_TASS = round2(ppTass);
        pp.PP_BTP = round2(ppBtp);
        pp.PP_TTOT = round2(ppTtot);
        pp.PP_NTPVPK = round2(ppNtp);
      }
      continue;
    } else if (drCoverage(pp)) {
      // Doorlopende reis: PP-level premiums (PP_TTOT/PP_NTP) from the BFF are
      // authoritative — there are no section-level totals to sum. Skip the
      // recalculation so the generic overwrite below doesn't zero them.
      continue;
    } else if (pp.OP) {
      // Woonhuis: base (OP) + glass (GP)
      sections = ['OP'];
      if (pp.GP) sections.push('GP');
      delete pp.VR; // VR not offered for SAA

      // Apply hidden woonhuis discount to section-level values before aggregation.
      // SAA: WoonhuisScreen bakes the discount into workflow data when applyToData=true, so
      // this block must not re-apply. With applyToData=true we treat the incoming data as
      // already discounted; with applyToData=false we leave data untouched (UI-only mode).
      const whDiscountPre = (config as any)?.woonhuisDiscount;
      const whAlreadyBaked = (pp as any)?._discountBaked === true || whDiscountPre?.applyToData === true;
      if (!whAlreadyBaked && whDiscountPre?.enabled && whDiscountPre?.percentage > 0 && whDiscountPre?.applyToData !== false) {
        const dFactor = 1 - whDiscountPre.percentage / 100;
        const excluded: string[] = whDiscountPre.excludeCoverages || [];
        for (const s of sections) {
          if (excluded.includes(s)) continue;
          const sec = Array.isArray(pp[s]) ? pp[s][0] : pp[s];
          if (sec) {
            for (const suffix of ['_NTP', '_TPP', '_TASS', '_BTP', '_TTOT']) {
              const key = `${s}${suffix}`;
              if (sec[key] !== undefined) sec[key] = Math.round(Number(sec[key]) * dFactor * 100) / 100;
            }
          }
        }
      }
    } else if (pp.DA) {
      // AVP: main section only — discount is UI-only, never applied to outgoing data
      sections = ['DA'];
    } else if (pp.OB) {
      // Personenauto: premiums live in WA, CA, and optional coverage sections (RP, SV, CG)
      sections = ['WA'];
      if (pp.CA) sections.push('CA');
      if (pp.RP) sections.push('RP');
      if (pp.SV) sections.push('SV');
      if (pp.CG) sections.push('CG');
    } else if (pp.WC) {
      // Rechtsbijstand: WC base premium is at PP level (WC_NTP=0), not section level.
      // Extract WC base by subtracting all optional section NTPs from BFF's PP totals.
      const origNTP = Number(pp.PP_NTP || 0);
      const origTPP = Number(pp.PP_TPP || 0);
      const origTASS = Number(pp.PP_TASS || 0);
      const origBTP = Number(pp.PP_BTP || 0);
      const origTTOT = Number(pp.PP_TTOT || 0);
      // Sum ALL optional section values (regardless of selection) to isolate WC base
      let allOptNTP = 0, allOptTPP = 0, allOptTASS = 0, allOptBTP = 0, allOptTTOT = 0;
      for (const optSec of ['VK', 'IN', 'FV']) {
        const s = pp[optSec];
        if (s) {
          allOptNTP += Number(s[`${optSec}_NTP`] || 0);
          allOptTPP += Number(s[`${optSec}_TPP`] || 0);
          allOptTASS += Number(s[`${optSec}_TASS`] || 0);
          allOptBTP += Number(s[`${optSec}_BTP`] || 0);
          allOptTTOT += Number(s[`${optSec}_TTOT`] || 0);
        }
      }
      if (Array.isArray(pp.RP)) {
        for (const rpItem of pp.RP) {
          allOptNTP += Number(rpItem?.RP_NTP || 0);
          allOptTPP += Number(rpItem?.RP_TPP || 0);
          allOptTASS += Number(rpItem?.RP_TASS || 0);
          allOptBTP += Number(rpItem?.RP_BTP || 0);
          allOptTTOT += Number(rpItem?.RP_TTOT || 0);
        }
      }
      // WC base = PP totals minus all optional sections
      const wcBaseNTP = origNTP - allOptNTP;
      const wcBaseTPP = origTPP - allOptTPP;
      const wcBaseTASS = origTASS - allOptTASS;
      const wcBaseBTP = origBTP - allOptBTP;
      const wcBaseTTOT = origTTOT - allOptTTOT;

      // Now sum only selected optional coverages
      sections = [];
      const rbDekkingen = opts.rbDekkingen || {};
      if (rbDekkingen.Verkeer === 'J' && pp.VK) sections.push('VK');
      if (rbDekkingen.Inkomen === 'J' && pp.IN) sections.push('IN');
      if (rbDekkingen.Fiscaal_Vermogen === 'J' && pp.FV) sections.push('FV');
      // RP is an array - handled separately below
      // Seed sums with WC base
      wcBaseSeed = { wcBaseNTP, wcBaseTPP, wcBaseTASS, wcBaseBTP, wcBaseTTOT };
    } else {
      sections = [];
    }
    let sumNTP = wcBaseSeed?.wcBaseNTP || 0;
    let sumTPP = wcBaseSeed?.wcBaseTPP || 0;
    let sumTASS = wcBaseSeed?.wcBaseTASS || 0;
    let sumBTP = wcBaseSeed?.wcBaseBTP || 0;
    let sumTTOT = wcBaseSeed?.wcBaseTTOT || 0;
    for (const s of sections) {
      const sec = Array.isArray(pp[s]) ? pp[s][0] : pp[s];
      if (sec) {
        sumNTP += Number(sec[`${s}_NTP`] || 0);
        sumTPP += Number(sec[`${s}_TPP`] || 0);
        sumTASS += Number(sec[`${s}_TASS`] || 0);
        sumBTP += Number(sec[`${s}_BTP`] || 0);
        sumTTOT += Number(sec[`${s}_TTOT`] || 0);
      }
    }
    // Rechtsbijstand RP: sum selected RP items from array
    if (pp.WC && Array.isArray(pp.RP)) {
      const rbDekkingen = opts.rbDekkingen || {};
      const rpCodeMap: Record<string, string> = {
        '9520': 'Eigendom_Verhuur_Onroerende_Zaken',
        '9530': 'Statutair_directeur',
        '9531': 'Bestuurder_Vereniging_van_Eigenaars',
        '9572': 'Niet_consumentenkoop',
        '9589': 'Financieel_onvermogen',
      };
      for (const rpItem of pp.RP) {
        const dekkingKey = rpCodeMap[rpItem?.RP_CODE];
        if (dekkingKey && rbDekkingen[dekkingKey] === 'J') {
          sumNTP += Number(rpItem?.RP_NTP || 0);
          sumTPP += Number(rpItem?.RP_TPP || 0);
          sumTASS += Number(rpItem?.RP_TASS || 0);
          sumBTP += Number(rpItem?.RP_BTP || 0);
          sumTTOT += Number(rpItem?.RP_TTOT || 0);
        }
      }
    }
    pp.PP_NTP = sumNTP;
    pp.PP_TPP = sumTPP;
    pp.PP_TASS = sumTASS;
    pp.PP_BTP = sumBTP;
    // PP_TTOT: for products with discounts applied to section values, recalculate from sums.
    // For inboedel (which skips this loop via continue), BFF value is kept as-is.
    if (pp.DA || pp.OP) {
      pp.PP_TTOT = sumTTOT;
      pp.PP_NTPVPK = sumNTP;
    }
  }

  // Strip PP_NTPVPK from contracts before package call (internal field, not in BFF schema)
  if (calculationDocument.Contract) {
    for (const contract of calculationDocument.Contract) {
      if (contract.PP) delete contract.PP.PP_NTPVPK;
    }
  }
};

/**
 * Re-assert the DFD products' input-only fields on a RESPONSE document. The package and quote
 * responses replace the working document wholesale (useOfferChain), and the clientservice does
 * not echo fields it doesn't price on: Ongevallen loses OD_COMBINA ('1'-'6') + the insured sums
 * and the verzekerde's VZ_ANAAM/VZ_VOORL, Doorlopende reis loses DR_MVZBMIL. Without this the
 * offer (quote) call and everything stored for the payment / acceptance / request steps go out
 * without them. Same rules as enrichOngevallenForSaa / enrichDoorlopendeReisForSaa; walks
 * Pakket[0].Onderdeel or Contract[] and mutates in place. Seam: opts.data (workflow step data).
 */
export const restoreDfdInputsOnResponse = (calculationDocument: any, opts: { data: any }): void => {
  const products =
    calculationDocument?.Pakket?.[0]?.Onderdeel ||
    calculationDocument?.Contract ||
    [];
  const relationVP = calculationDocument?.Relatiemantel?.VP || {};
  const clientInfo = opts.data?.['client-information']?.clientInfo || {};
  for (const product of products) {
    const pp = product?.PP;
    if (!pp) continue;
    if (pp.DA || pp.IP || pp.OP || pp.OB || pp.WC) continue; // not a DFD product
    if (pp.DR) {
      pp.DR = restoreDoorlopendeReisInputs(pp.DR);
    } else if (pp.OD) {
      pp.OD = restoreOngevallenInputs(pp.OD, opts.data?.ongevallen?.values);
      const vz = restoreOngevallenInsured(pp.VZ, {
        lastName: relationVP.VP_ANAAM ?? clientInfo.lastName ?? pp.VP?.VP_ANAAM,
        initials: relationVP.VP_VOORL ?? clientInfo.initials,
        gebdat: pp.VP?.VP_GEBDAT ?? relationVP.VP_GEBDAT,
      });
      if (Object.keys(vz).length > 0) pp.VZ = vz;
    }
  }
};

/**
 * Clean unselected coverages from the enriched document before storing.
 * The package endpoint returns ALL coverage sections, but we only want selected ones.
 * Checks where the products live: Pakket[0].Onderdeel or Contract[].
 * Mutates calculationDocument in place. Seams: opts.{data, config, selectedProducts, ppEkst}
 * replacing the component closure.
 */
export const cleanPackageResponseProducts = (
  calculationDocument: any,
  opts: { data: any; config: any; selectedProducts: any; ppEkst: number; inboedelGlassAllowed: boolean },
): void => {
  const { data, config, selectedProducts, ppEkst, inboedelGlassAllowed } = opts;
  const hasPakketProducts = calculationDocument?.Pakket?.[0]?.Onderdeel?.length > 0;
  const productArray = hasPakketProducts
    ? calculationDocument.Pakket[0].Onderdeel
    : calculationDocument?.Contract || [];

  if (productArray.length > 0) {
    const cleanedProducts = productArray.map((product: any) => {
      if (!product?.PP) return product;

      // Get the corresponding workflow data to check selections
      // IMPORTANT: Check IP before OP — inboedel has BOTH IP and OP sections,
      // while woonhuis has only OP. Checking OP first misidentifies inboedel as woonhuis.
      let workflowProductData = null;
      if (product.PP.IP) {
        workflowProductData = data['inboedel'];
      } else if (product.PP.OP) {
        workflowProductData = data['woonhuis'];
      } else if (product.PP.DA) {
        workflowProductData = data['aansprakelijkheid'];
      } else if (product.PP.OB) {
        workflowProductData = data['personenauto'];
      }

      if (!workflowProductData) return product;

      const dekkingen = workflowProductData?.Supportsection?.Dekkingen || workflowProductData?._uiState?.Dekkingen || {};

      // For inboedel: apply hidden discount, clean unselected coverages, recalculate totals
      if (product.PP.IP) {
        // Apply hidden inboedel discount to section-level values — but ONLY when the data
        // isn't already discounted. With applyToData=true (SAA) InboedelScreen already baked
        // it, so re-applying here double-discounts (×0.95²). The _discountBaked marker is
        // stripped by the schema whitelist, so fall back to applyToData (same guard as above).
        const ibDiscountClean = (config as any)?.inboedelDiscount;
        const ibCleanAlreadyBaked = (product.PP as any)?._discountBaked === true || ibDiscountClean?.applyToData === true;
        if (!ibCleanAlreadyBaked && ibDiscountClean?.enabled && ibDiscountClean?.percentage > 0 && ibDiscountClean?.applyToData !== false) {
          const ibF = 1 - ibDiscountClean.percentage / 100;
          const ibEx: string[] = ibDiscountClean.excludeCoverages || [];
          const ibR = (v: number) => Math.round(v * ibF * 100) / 100;
          for (const s of ['IP', 'CY', 'BI', 'GP', 'LS', 'OP', 'HU', 'KB']) {
            if (ibEx.includes(s)) continue;
            const sec = Array.isArray(product.PP[s]) ? product.PP[s][0] : product.PP[s];
            if (!sec) continue;
            for (const suffix of ['_NTP', '_TPP', '_TASS', '_TTOT']) {
              const key = `${s}${suffix}`;
              if (sec[key] !== undefined) sec[key] = ibR(Number(sec[key]));
            }
          }
          if (!ibEx.includes('EC') && Array.isArray(product.PP.EC)) {
            for (const ec of product.PP.EC) {
              for (const suffix of ['_NTP', '_TPP', '_TASS', '_TTOT']) {
                const key = `EC${suffix}`;
                if (ec[key] !== undefined) ec[key] = ibR(Number(ec[key]));
              }
            }
          }
        }

        // Delete unselected optional coverage sections. Glas also goes when this address may
        // not carry glass (inboedelGlassAllowed), whatever Dekkingen says: this runs on the
        // PACKAGE RESPONSE, and the offer chain can reach here from a sessionStorage document
        // enriched earlier — that path skips enrich.ts entirely. See utils/glassPremium.ts.
        if (product.PP.GP && (dekkingen.Glas !== 'J' || !inboedelGlassAllowed)) delete product.PP.GP;
        if (dekkingen.Lijfsieraden !== 'J' && product.PP.LS) delete product.PP.LS;
        // ONE EC row (schema maxItems 1): Smart Home replaces Mobiele elektronica.
        keepSelectedEcRow(product.PP, dekkingen);
        if (dekkingen.Huurdersbelang !== 'J' && product.PP.HU) delete product.PP.HU;
        if (dekkingen.Bijzonderebezittingen !== 'J' && product.PP.KB) delete product.PP.KB;
        // GA and VR no longer offered
        delete product.PP.GA;
        delete product.PP.VR;

        // Recalculate PP totals from discounted + filtered sections
        let ibNtp = 0, ibTpp = 0, ibTass = 0, ibTtot = 0;
        for (const s of ['IP', 'CY', 'BI', 'GP', 'LS', 'OP', 'HU', 'KB']) {
          const sec = Array.isArray(product.PP[s]) ? product.PP[s][0] : product.PP[s];
          if (sec) {
            ibNtp += Number(sec[`${s}_NTP`] || 0);
            ibTpp += Number(sec[`${s}_TPP`] || 0);
            ibTass += Number(sec[`${s}_TASS`] || 0);
            ibTtot += Number(sec[`${s}_TTOT`] || 0);
          }
        }
        if (Array.isArray(product.PP.EC)) {
          for (const ec of product.PP.EC) {
            ibNtp += Number(ec.EC_NTP || 0);
            ibTpp += Number(ec.EC_TPP || 0);
            ibTass += Number(ec.EC_TASS || 0);
            ibTtot += Number(ec.EC_TTOT || 0);
          }
        }
        // Round to 2 decimals — section sums can land as 4.830000000000001 etc. from JS float.
        const round2 = (v: number) => Math.round(v * 100) / 100;
        product.PP.PP_NTP = round2(ibNtp);
        product.PP.PP_TPP = round2(ibTpp);
        product.PP.PP_TASS = round2(ibTass);
        product.PP.PP_TTOT = round2(ibTtot);
        product.PP.PP_NTPVPK = round2(ibNtp);
      }

      // For woonhuis: clean unselected coverages, apply hidden discount, and recalculate totals
      // Skip if product has IP (that's inboedel, not woonhuis - inboedel has both IP and OP)
      if (product.PP.OP && !product.PP.IP) {
        // OP can be array (from BFF/schema) or object (from UI) - normalize
        const opData = Array.isArray(product.PP.OP) ? product.PP.OP[0] : product.PP.OP;

        // Hidden woonhuis discount from config (applied to non-excluded coverages)
        const whDiscount = (config as any)?.woonhuisDiscount;
        const discountEnabled = whDiscount?.enabled && whDiscount?.percentage > 0 && whDiscount?.applyToData !== false;
        const discountFactor = discountEnabled ? 1 - whDiscount.percentage / 100 : 1;
        const excludedCoverages: string[] = whDiscount?.excludeCoverages || [];

        const applyDiscount = (val: number, section: string) =>
          discountEnabled && !excludedCoverages.includes(section)
            ? Math.round(val * discountFactor * 100) / 100
            : val;

        // Get base OP premiums (without optional coverages) - apply discount
        const baseNTP = applyDiscount(Number(opData?.OP_NTP || 0), 'OP');
        const baseTPP = applyDiscount(Number(opData?.OP_TPP || 0), 'OP');
        const baseTASS = applyDiscount(Number(opData?.OP_TASS || 0), 'OP');
        const baseTTOT = applyDiscount(Number(opData?.OP_TTOT || 0), 'OP');

        // Re-seed OP_BTP from workflow data (BFF package endpoint truncates BTP to 2
        // decimals, costing 1¢ on the displayed total via computeWoonhuisDisplayPremium).
        const workflowOpBtp = Number(workflowProductData?.Contractdocument?.PP?.OP?.OP_BTP);
        if (opData && isFinite(workflowOpBtp) && workflowOpBtp > 0) {
          opData.OP_BTP = workflowOpBtp;
        }

        // Write discounted values back to section
        if (opData) {
          if (opData.OP_NTP !== undefined) opData.OP_NTP = baseNTP;
          if (opData.OP_TPP !== undefined) opData.OP_TPP = baseTPP;
          if (opData.OP_TASS !== undefined) opData.OP_TASS = baseTASS;
          if (opData.OP_TTOT !== undefined) opData.OP_TTOT = baseTTOT;
        }

        // Normalize OP to object (BFF returns array per schema, but rest of code expects object)
        if (Array.isArray(product.PP.OP)) {
          product.PP.OP = product.PP.OP[0] || {};
        }

        // Get all optional coverage premium values before deleting - apply discount
        const glassNTP = applyDiscount(product.PP.GP?.GP_NTP || 0, 'GP');
        const glassTPP = applyDiscount(product.PP.GP?.GP_TPP || 0, 'GP');
        const glassTASS = applyDiscount(product.PP.GP?.GP_TASS || 0, 'GP');
        const glassTTOT = applyDiscount(product.PP.GP?.GP_TTOT || 0, 'GP');

        // Re-seed GP_BTP from workflow data (same precision concern as OP_BTP above).
        const workflowGpBtp = Number(workflowProductData?.Contractdocument?.PP?.GP?.GP_BTP);
        if (product.PP.GP && isFinite(workflowGpBtp) && workflowGpBtp > 0) {
          product.PP.GP.GP_BTP = workflowGpBtp;
        }

        // Write discounted values back to GP section
        if (product.PP.GP) {
          if (product.PP.GP.GP_NTP !== undefined) product.PP.GP.GP_NTP = glassNTP;
          if (product.PP.GP.GP_TPP !== undefined) product.PP.GP.GP_TPP = glassTPP;
          if (product.PP.GP.GP_TASS !== undefined) product.PP.GP.GP_TASS = glassTASS;
          if (product.PP.GP.GP_TTOT !== undefined) product.PP.GP.GP_TTOT = glassTTOT;
        }

        // Delete unselected coverage sections
        if (dekkingen.Glas !== 'J' && product.PP.GP) delete product.PP.GP;

        // Recalculate all premium fields with base + only selected coverages
        const selectedNTP = (dekkingen.Glas === 'J' ? glassNTP : 0);
        const selectedTPP = (dekkingen.Glas === 'J' ? glassTPP : 0);
        const selectedTASS = (dekkingen.Glas === 'J' ? glassTASS : 0);
        const selectedTTOT = (dekkingen.Glas === 'J' ? glassTTOT : 0);

        // Update all PP premium fields
        product.PP.PP_NTP = baseNTP + selectedNTP;
        product.PP.PP_TPP = baseTPP + selectedTPP;
        product.PP.PP_TASS = baseTASS + selectedTASS;
        product.PP.PP_TTOT = baseTTOT + selectedTTOT;
        product.PP.PP_NTPVPK = product.PP.PP_NTP; // Net premium before package discount
      }

      // AVP: BFF rounds PP_* fields to 2 decimals on its round-trip but preserves DA_* at
      // full precision. Re-mirror DA -> PP so the stored document is internally consistent
      // (PP_BTP 3.145 matches DA_BTP 3.145, not the BFF-rounded 3.15). Scoped to AVP-only
      // products by the DA presence + absence of other section discriminators.
      if (product.PP.DA && !product.PP.IP && !product.PP.OP && !product.PP.OB && !product.PP.WC) {
        const da = product.PP.DA;
        if (da.DA_BTP !== undefined) product.PP.PP_BTP = Number(da.DA_BTP);
        if (da.DA_NTP !== undefined) {
          product.PP.PP_NTP = Number(da.DA_NTP);
          product.PP.PP_NTPVPK = Number(da.DA_NTP);
        }
        if (da.DA_TPP !== undefined) product.PP.PP_TPP = Number(da.DA_TPP);
        if (da.DA_TASS !== undefined) product.PP.PP_TASS = Number(da.DA_TASS);
        if (da.DA_TTOT !== undefined) product.PP.PP_TTOT = Number(da.DA_TTOT);
      }

      return product;
    });

    // Write cleaned products back to the correct location
    if (hasPakketProducts) {
      calculationDocument.Pakket[0].Onderdeel = cleanedProducts;
    } else {
      calculationDocument.Contract = cleanedProducts;
    }

    // Re-stamp coverage VOLGNUM after the coverage filtering above: deleting sections leaves
    // gaps in the order (main=1, then glas / extras), which must be re-applied here before the
    // document is stored and quoted.
    orderSaaCoveragesInDocument(calculationDocument);

    // Recalculate PK (package) totals from cleaned product PP values.
    // The BFF computed PK_TTOT from inflated PP values (including auxiliary sections).
    // After coverage cleaning, PP_TTOT reflects only user-facing premiums.
    const pk = calculationDocument?.Pakket?.[0]?.Mantel?.PK;
    if (pk) {
      let pkNTP = 0, pkTPP = 0, pkTASS = 0, pkBTP = 0, pkTTOT = 0;
      for (const p of cleanedProducts) {
        if (p?.PP) {
          pkNTP += Number(p.PP.PP_NTP || 0);
          pkTPP += Number(p.PP.PP_TPP || 0);
          pkTASS += Number(p.PP.PP_TASS || 0);
          pkBTP += Number(p.PP.PP_BTP || 0);
          pkTTOT += Number(p.PP.PP_TTOT || 0);
        }
      }
      pk.PK_NTP = pkNTP;
      pk.PK_TPP = pkTPP;
      pk.PK_TASS = pkTASS;
      pk.PK_BTP = pkBTP;
      pk.PK_TTOT = pkTTOT;
      pk.PK_NTPVPK = pkNTP;

      // PK_TINV (Servicevergoeding) - SAA charges this once at the package level
      // for the whole package regardless of product count. BFF doesn't add it,
      // so we apply the fee + 21% assurantiebelasting on top of the package totals here.
      // Toggleable via prolongationFee.enabled in config.
      applyPkTinv(pk, ppEkst);

      // PP_EKST per product: one-time poliskosten (€12,50 car / €6,25 other, capped at
      // €12,50 per package). BFF returns 0 for this readOnly field, so we allocate after
      // the post-merge surcharge. Toggleable via policyCost.enabled in config.
      const policyAllocation = computePolicyCostAllocation(selectedProducts, config);
      applyPolicyCostAllocationToProducts(cleanedProducts, policyAllocation);
    }
  }

  // The package response replaced the working document and is what /quote receives next —
  // the DFD input-only fields must survive it (see the helper).
  restoreDfdInputsOnResponse(calculationDocument, { data });
};

/**
 * Drop the Inboedel glass section while this address may not carry glass (inboedelGlassAllowed:
 * config switch, another product covering glass, or no Infofolio-known rebuild value).
 *
 * LAST GATE BEFORE THE WIRE, and the only one that covers /quote on every path. The offer chain
 * can reach the quote call with a document that passed through NEITHER enrich.ts NOR
 * cleanPackageResponseProducts: when a calculation id AND the package-discount flag are both
 * already in sessionStorage, step 1 loads the stored document, step 2 is skipped by
 * `!storedPackageDiscountFlag` (and its else-if needs `!calculationDocument`, which is now set),
 * so step 3 sends it as-is. Config and the Infofolio marker live outside that document, so a
 * document stored before they changed survives a reload and would carry GP into the one call
 * that can decline the whole quote for an unratable section.
 *
 * Deliberately NOT `cleanPackageResponseProducts`: that recomputes totals and applies PK_TINV +
 * poliskosten, which would double-charge an already-cleaned stored document. This does one
 * thing, is idempotent, and is safe to call on any document shape.
 */
export const dropDisabledInboedelGlass = (calculationDocument: any, inboedelGlassAllowed: boolean): void => {
  if (inboedelGlassAllowed) return;
  const products =
    calculationDocument?.Pakket?.[0]?.Onderdeel ||
    calculationDocument?.Contract ||
    [];
  for (const product of products) {
    // Inboedel discriminator: has IP. Woonhuis (OP only) and AVP (DA) keep their glass.
    if (product?.PP?.IP && product.PP.GP) delete product.PP.GP;
  }
};

/**
 * BFF truncates BTPs to 2 decimals on the quote response, costing 1¢ on
 * computeWoonhuisDisplayPremium (sum-then-floor × 1.21). Re-seed OP_BTP / GP_BTP
 * from the full-precision workflow data so the breakdown card matches the
 * Woonhuis screen badge. Also drops the Inboedel glass section when it is not
 * selected or is switched off. Mutates calculationDocument in place (the quote-response
 * adoption `calculationDocument = quoteResponse.Relatiedocument` stays in the screen).
 */
export const reseedQuoteResponsePremiums = (
  calculationDocument: any,
  opts: { data: any; inboedelGlassAllowed: boolean },
): void => {
  const { data, inboedelGlassAllowed } = opts;
  const quoteProducts =
    calculationDocument?.Pakket?.[0]?.Onderdeel ||
    calculationDocument?.Contract ||
    [];
  for (const product of quoteProducts) {
    if (!product?.PP) continue;
    const isInboedel = !!product.PP.IP;
    const isWoonhuis = !!product.PP.OP && !product.PP.IP;

    // Re-seed OP_BTP for woonhuis at full precision (BFF truncates BTPs to 2 decimals).
    if (isWoonhuis) {
      const opSec = Array.isArray(product.PP.OP) ? product.PP.OP[0] : product.PP.OP;
      const workflowOpBtp = Number(data?.['woonhuis']?.Contractdocument?.PP?.OP?.OP_BTP);
      if (opSec && isFinite(workflowOpBtp) && workflowOpBtp > 0) {
        opSec.OP_BTP = workflowOpBtp;
      }
    }

    // Glass (GP) is rated by the Midglas engine, so the quote response carries it. Drop the
    // section when the customer didn't select it, or when this address may not carry glass
    // whatever Dekkingen says (Woonhuis: Glas is always on). This document is stored and read
    // back by ConfirmationScreen for the application, so a GP left here reaches the backend.
    if (isInboedel) {
      const dekkingen = data?.inboedel?.Supportsection?.Dekkingen
        || data?.inboedel?._uiState?.Dekkingen || {};
      if (dekkingen.Glas !== 'J' || !inboedelGlassAllowed) delete product.PP.GP;
      keepSelectedEcRow(product.PP, dekkingen);
    }
  }

  // The quote response replaced the working document and is what the payment / acceptance /
  // request steps read back — the DFD input-only fields must survive it (see the helper).
  restoreDfdInputsOnResponse(calculationDocument, { data });
};

/**
 * Extract VP_IDNR from a createRelation response - check multiple possible locations.
 * The backend may return the ID under different property names.
 */
export const extractVpIdnr = (relationResponse: any): any =>
  // Root level properties
  relationResponse?.VP_IDNR ||
  relationResponse?.RelationId ||
  relationResponse?.relationId ||
  relationResponse?.id ||
  // Nested in data wrapper (common BFF pattern)
  relationResponse?.data?.VP_IDNR ||
  relationResponse?.data?.RelationId ||
  relationResponse?.data?.relationId ||
  relationResponse?.data?.id ||
  // Nested in Relatiemantel
  relationResponse?.Relatiemantel?.VP_IDNR ||
  relationResponse?.Relatiemantel?.VP?.VP_IDNR ||
  relationResponse?.Relatiemantel?.id ||
  // Deep nested in Relatiedocument
  relationResponse?.Relatiedocument?.Relatiemantel?.VP?.VP_IDNR ||
  relationResponse?.Relatiedocument?.VP_IDNR ||
  null;

/** Extract VP data from possible locations in a createRelation response. */
export const extractVpData = (relationResponse: any): any =>
  relationResponse?.Relatiedocument?.Relatiemantel?.VP ||
  relationResponse?.Relatiemantel?.VP ||
  relationResponse?.data?.Relatiedocument?.Relatiemantel?.VP ||
  relationResponse?.data?.Relatiemantel?.VP ||
  relationResponse?.Relatiemantel ||
  {};

/** Extract the calculation ID (calculationRecordId) from a calculateRelation response. */
export const extractCalculationId = (calculationResponse: any): any =>
  calculationResponse?.calculationRecordId ||
  calculationResponse?.CalculationRecordId ||
  calculationResponse?.calculation_record_id ||
  calculationResponse?.Contract?.[0]?.AL?.SK?.SK_ZKSLTL;

/** Extract the quote ID from a generateQuote response - try multiple possible field names. */
export const extractQuoteId = (quoteResponse: any): any =>
  quoteResponse?.QuoteId ||
  quoteResponse?.quoteId ||
  quoteResponse?.quote_id ||
  quoteResponse?.QUOTE_ID ||
  quoteResponse?.id ||
  quoteResponse?.Id ||
  quoteResponse?.ID ||
  quoteResponse?.Offerte?.OT_IDNR ||
  quoteResponse?.offerte?.ot_idnr;

/** Build a minimal VP data structure from workflow client info (fallback when createRelation returned no VP). */
export const buildFallbackVpData = (clientInfo: any, vpIdnr: any): any => {
  const vpData = {
    VP_IDNR: vpIdnr,
    VP_VOORL: clientInfo.initials,
    VP_ANAAM: clientInfo.lastName,
    VP_VOORV: clientInfo.affix,
    VP_GESLACH: clientInfo.gender,
    VP_EMAIL: clientInfo.email,
    VP_TELNUM: clientInfo.phone,
    // VP_AANHEFC / VP_TITULAT — zero-padded 5-char gender codes ('00001' / '00002'),
    // mirrors the derivation in createRelation (clientService.ts).
    VP_AANHEFC: clientInfo.salutationCode
      || (clientInfo.gender === 'M' ? '00001' : clientInfo.gender === 'V' ? '00002' : undefined),
    VP_TITULAT: clientInfo.titulature
      || (clientInfo.gender === 'M' ? '00001' : clientInfo.gender === 'V' ? '00002' : undefined),
  } as any;

  // Add date of birth if available
  if (clientInfo.dateOfBirth) {
    const parts = clientInfo.dateOfBirth.split('-');
    const day = parts[0];
    const month = parts[1];
    const year = parts[2];
    vpData.VP_GEBDAT = Number(`${year}${month}${day}`);
  }

  return vpData;
};

/** UI discount factor for display (when applyToData is false, data is undiscounted). */
export const displayDiscountFactor = (cfg: any) =>
  cfg?.enabled && cfg?.percentage > 0 && cfg?.applyToData === false
    ? 1 - cfg.percentage / 100 : 1;

/**
 * Get package discount information and new premium (using TTOT = total with taxes/costs).
 * Pure body of the screen's getPackageDiscount: the parsed enriched document is passed in
 * (readEnrichedDocument()) instead of the sessionStorage read.
 */
export const extractPackageDiscount = (
  doc: any,
  factors: { whFactor: number; ibFactor: number; avpFactor: number },
): { percentage: number; amount: number; newMonthlyPremium: number } | null => {
  const { whFactor, ibFactor, avpFactor } = factors;
  try {
    if (!doc) return null;

    const pakket = doc?.Pakket?.[0]?.Mantel?.PK;

    if (!pakket) return null;

    const percentage = Number(pakket.PK_PRCPKKT || 0);
    const amount = Number(pakket.PK_TPAKKRT || 0);
    let newMonthlyPremium = Number(pakket.PK_TTOT || 0);

    // Apply hidden UI discounts to package total when applyToData is false
    // PK_TTOT is computed from undiscounted product premiums, so we need to
    // recalculate from individual products with per-product discount factors
    if (whFactor < 1 || ibFactor < 1 || avpFactor < 1) {
      const products = doc?.Pakket?.[0]?.Onderdeel || doc?.Contract || [];
      let discountedTotal = 0;
      for (const p of products) {
        if (!p?.PP) continue;
        const ppTtot = Number(p.PP.PP_TTOT || 0);
        if (p.PP.IP) {
          discountedTotal += Math.round(ppTtot * ibFactor * 100) / 100;
        } else if (p.PP.DA && !p.PP.OP && !p.PP.IP) {
          discountedTotal += Math.round(ppTtot * avpFactor * 100) / 100;
        } else if (p.PP.OP && !p.PP.IP) {
          discountedTotal += Math.round(ppTtot * whFactor * 100) / 100;
        } else {
          discountedTotal += ppTtot;
        }
      }
      newMonthlyPremium = discountedTotal;
    }

    // Only return discount if percentage is above 0.01 (already in percentage form, e.g., 1 = 1%)
    if (percentage > 0.01) {
      return {
        percentage: percentage,
        amount: amount,
        newMonthlyPremium: newMonthlyPremium
      };
    }

    return null;
  } catch (error) {
    return null;
  }
};
